import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit, Trash2, Shield, IdCard, KeyRound, Wallet, Building2 } from 'lucide-react';
import { getCurrentCompanyId } from '../lib/company';
import { useAuth } from '../contexts/AuthContext';
import { APP_MODULES } from '../lib/modules';
import { toast } from 'sonner';

const EMPTY_PROFILE = {
  cedula: '', phone: '', address: '', birthdate: '', emergency_contact: '',
  bank_name: '', bank_account: '', account_type: 'ahorro',
  mobile_payment_phone: '', mobile_payment_id: '',
  salary: '', commission_rate: '', notes: ''
};

export default function Users() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isMaster = ['master', 'superadmin'].includes(currentUser?.role_id);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileForm, setProfileForm] = useState(EMPTY_PROFILE);
  const [companies, setCompanies] = useState([]);
  const [includeEmployeeData, setIncludeEmployeeData] = useState(false);
  const [newProfile, setNewProfile] = useState(EMPTY_PROFILE);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role_id: '',
    company_id: ''
  });

  const loadData = async () => {
    try {
      const [usersRes, rolesRes, compsRes] = await Promise.all([
        api.getUsers({ company_id: getCurrentCompanyId() }),
        api.getRoles(),
        api.getCompanies(true)
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setCompanies(compsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    (async () => { await loadData(); })();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const companyName = (id) => companies.find(c => c.company_id === id)?.name || id;

  const cleanProfile = (p) => {
    const out = {};
    Object.entries(p).forEach(([k, v]) => {
      if (v === '' || v === null) return;
      if (k === 'salary' || k === 'commission_rate') out[k] = parseFloat(v);
      else out[k] = v;
    });
    return out;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const company_id = formData.company_id || getCurrentCompanyId();
    if (!company_id) {
      toast.error('SalesPRO: Selecciona una empresa para el usuario');
      return;
    }
    try {
      const payload = { ...formData, company_id, allowed_companies: [company_id] };
      const res = await api.createUser(payload);
      const newUserId = res.data?.user_id;
      if (includeEmployeeData && newUserId) {
        const cleaned = cleanProfile(newProfile);
        if (Object.keys(cleaned).length > 0) {
          await api.updateEmployeeProfile(newUserId, { user_id: newUserId, ...cleaned });
        }
      }
      toast.success('SalesPRO: Usuario creado');
      setDialogOpen(false);
      loadData();
      setFormData({ email: '', password: '', full_name: '', role_id: '', company_id: '' });
      setNewProfile(EMPTY_PROFILE);
      setIncludeEmployeeData(false);
      setShowPassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SalesPRO: Error al crear usuario');
    }
  };

  const openProfile = async (user) => {
    setProfileUser(user);
    setProfileForm(EMPTY_PROFILE);
    setProfileOpen(true);
    try {
      const res = await api.getEmployeeProfile(user.user_id);
      if (res.data && Object.keys(res.data).length > 0) {
        setProfileForm({ ...EMPTY_PROFILE, ...res.data, salary: res.data.salary ?? '', commission_rate: res.data.commission_rate ?? '' });
      }
    } catch (e) { /* sin ficha aún */ }
  };

  const saveProfile = async () => {
    try {
      await api.updateEmployeeProfile(profileUser.user_id, { user_id: profileUser.user_id, ...cleanProfile(profileForm) });
      toast.success('SalesPRO: Ficha del empleado guardada');
      setProfileOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SalesPRO: Error al guardar ficha');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api.updateUser(selectedUser.user_id, {
        full_name: selectedUser.full_name,
        role_id: selectedUser.role_id,
        active: selectedUser.active,
        company_id: selectedUser.company_id || null,
        allowed_companies: selectedUser.allowed_companies || [],
        enabled_modules: selectedUser.enabled_modules ?? null
      });
      toast.success('Usuario actualizado');
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      const d = error.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Error al actualizar usuario');
    }
  };

  const toggleAllowedCompany = (cid) => {
    setSelectedUser(prev => {
      const cur = prev.allowed_companies || [];
      return { ...prev, allowed_companies: cur.includes(cid) ? cur.filter(x => x !== cid) : [...cur, cid] };
    });
  };

  const isModuleOn = (key) => {
    const cur = selectedUser?.enabled_modules;
    if (!Array.isArray(cur)) return true; // null = sin restricción = todos visibles
    return cur.includes(key);
  };

  const toggleModule = (key) => {
    setSelectedUser(prev => {
      let cur = Array.isArray(prev.enabled_modules) ? [...prev.enabled_modules] : APP_MODULES.map(m => m.key);
      cur = cur.includes(key) ? cur.filter(x => x !== key) : [...cur, key];
      const allOn = APP_MODULES.every(m => cur.includes(m.key));
      return { ...prev, enabled_modules: allOn ? null : cur };
    });
  };

  const enableAllModules = () => setSelectedUser(prev => ({ ...prev, enabled_modules: null }));

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
    
    try {
      await api.deleteUser(userId);
      toast.success('Usuario eliminado');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar usuario');
    }
  };

  const handleEditClick = (user) => {
    setSelectedUser({...user});
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="users-loading">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-2 text-sm text-gray-600">Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#022C22]">Usuarios</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestiona los usuarios del sistema</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/payroll')} data-testid="goto-payroll-button">
            <Wallet size={16} className="mr-1.5" />
            Nómina
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" data-testid="add-user-button">
                <Plus size={16} className="mr-1.5" />
                Nuevo Usuario
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="user-dialog" className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nuevo Usuario</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="full_name">Nombre Completo</Label>
                <Input
                  id="full_name"
                  data-testid="user-name-input"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="user-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="user-password-input"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-primary font-medium"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? 'Ocultar' : 'Ver'}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="role">Rol</Label>
                <select
                  id="role"
                  value={formData.role_id}
                  onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="user-role-select"
                  required
                >
                  <option value="">Selecciona un rol</option>
                  {roles.map((role) => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="user-company">Empresa</Label>
                <select
                  id="user-company"
                  value={formData.company_id || getCurrentCompanyId() || ''}
                  onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="user-company-select"
                  disabled={!isMaster}
                  required
                >
                  <option value="">Selecciona una empresa</option>
                  {companies.map((c) => (
                    <option key={c.company_id} value={c.company_id}>{c.name}</option>
                  ))}
                </select>
                {!isMaster && <p className="text-xs text-[#64748B] mt-1">El usuario se creará en tu empresa actual.</p>}
              </div>

              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="include-employee"
                  checked={includeEmployeeData}
                  onChange={(e) => setIncludeEmployeeData(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                  data-testid="include-employee-data"
                />
                <label htmlFor="include-employee" className="text-sm font-medium">Agregar datos de empleado (banco, salario...)</label>
              </div>

              {includeEmployeeData && (
                <div className="space-y-3 border border-[#E2E8F0] rounded-md p-3 bg-[#F8FAFC] max-h-[40vh] overflow-y-auto">
                  <p className="text-xs font-semibold text-[#64748B] uppercase">Datos personales</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Cédula" value={newProfile.cedula} onChange={(e) => setNewProfile({ ...newProfile, cedula: e.target.value })} data-testid="np-cedula" />
                    <Input placeholder="Teléfono" value={newProfile.phone} onChange={(e) => setNewProfile({ ...newProfile, phone: e.target.value })} data-testid="np-phone" />
                  </div>
                  <Input placeholder="Dirección" value={newProfile.address} onChange={(e) => setNewProfile({ ...newProfile, address: e.target.value })} data-testid="np-address" />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Fecha nacimiento</Label>
                      <Input type="date" value={newProfile.birthdate} onChange={(e) => setNewProfile({ ...newProfile, birthdate: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Contacto emergencia</Label>
                      <Input value={newProfile.emergency_contact} onChange={(e) => setNewProfile({ ...newProfile, emergency_contact: e.target.value })} />
                    </div>
                  </div>
                  <p className="text-xs font-semibold text-[#64748B] uppercase pt-1">Datos bancarios</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Banco" value={newProfile.bank_name} onChange={(e) => setNewProfile({ ...newProfile, bank_name: e.target.value })} data-testid="np-bank" />
                    <Input placeholder="Nº de cuenta" value={newProfile.bank_account} onChange={(e) => setNewProfile({ ...newProfile, bank_account: e.target.value })} />
                  </div>
                  <select value={newProfile.account_type} onChange={(e) => setNewProfile({ ...newProfile, account_type: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                    <option value="ahorro">Ahorro</option>
                    <option value="corriente">Corriente</option>
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Pago móvil (tel)" value={newProfile.mobile_payment_phone} onChange={(e) => setNewProfile({ ...newProfile, mobile_payment_phone: e.target.value })} data-testid="np-mp-phone" />
                    <Input placeholder="Pago móvil (cédula)" value={newProfile.mobile_payment_id} onChange={(e) => setNewProfile({ ...newProfile, mobile_payment_id: e.target.value })} />
                  </div>
                  <p className="text-xs font-semibold text-[#64748B] uppercase pt-1">Remuneración</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" step="0.01" placeholder="Salario ($)" value={newProfile.salary} onChange={(e) => setNewProfile({ ...newProfile, salary: e.target.value })} data-testid="np-salary" />
                    <Input type="number" step="0.01" placeholder="Comisión (%)" value={newProfile.commission_rate} onChange={(e) => setNewProfile({ ...newProfile, commission_rate: e.target.value })} />
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full" data-testid="submit-user-button">
                Crear Usuario
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Users List (cards responsive) */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="users-list">
        {users.map((user) => {
          const userRole = roles.find(r => r.role_id === user.role_id);
          const perms = userRole?.permissions || [];
          const isAllAccess = perms.includes('all');
          const authCompanies = [...new Set([user.company_id, ...(user.allowed_companies || [])].filter(Boolean))];
          return (
            <Card key={user.user_id} className="border border-[#E2E8F0] shadow-sm" data-testid={`user-row-${user.user_id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#022C22] truncate">{user.full_name}</p>
                    <p className="text-xs text-[#64748B] truncate">{user.email}</p>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${user.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {user.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#F1F5F9] rounded-md text-xs">
                    <Shield size={13} /> {userRole?.role_name || user.role_id}
                  </span>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1 flex items-center gap-1"><Building2 size={11} /> Empresas autorizadas</p>
                  <div className="flex flex-wrap gap-1">
                    {['master', 'superadmin'].includes(user.role_id) ? (
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">Todas las empresas</span>
                    ) : authCompanies.length > 0 ? (
                      authCompanies.map(cid => (
                        <span key={cid} className="px-2 py-0.5 bg-[#EEF2FF] text-[#4338CA] rounded text-xs">{companyName(cid)}</span>
                      ))
                    ) : (
                      <span className="text-xs text-[#94A3B8]">Sin empresa asignada</span>
                    )}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[#94A3B8] mb-1">Permisos</p>
                  <div className="flex flex-wrap gap-1">
                    {isAllAccess ? (
                      <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs">Acceso total</span>
                    ) : perms.length > 0 ? (
                      <>
                        {perms.slice(0, 4).map(p => (
                          <span key={p} className="px-2 py-0.5 bg-[#F1F5F9] text-[#475569] rounded text-[11px]">{p}</span>
                        ))}
                        {perms.length > 4 && <span className="px-2 py-0.5 text-[11px] text-[#94A3B8]">+{perms.length - 4} más</span>}
                      </>
                    ) : (
                      <span className="text-xs text-[#94A3B8]">Sin permisos</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-end gap-1 pt-2 border-t border-[#E2E8F0]">
                  <Button variant="ghost" size="sm" title="Regenerar clave" data-testid={`reset-pass-${user.user_id}`} onClick={async () => { try { const r = (await api.resetUserPassword(user.user_id)).data; toast.success(`SalesPRO: Clave temporal de ${r.email}: ${r.temp_password} (deberá cambiarla al entrar)`, { duration: 20000 }); } catch (e) { toast.error(e.response?.data?.detail || 'SalesPRO: Error'); } }}><KeyRound size={16} className="text-[#B8860B]" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => openProfile(user)} title="Ficha de empleado" data-testid={`profile-user-${user.user_id}`}>
                    <IdCard size={16} className="text-[#022C22]" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleEditClick(user)} data-testid={`edit-user-${user.user_id}`}>
                    <Edit size={16} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(user.user_id)} data-testid={`delete-user-${user.user_id}`}>
                    <Trash2 size={16} className="text-red-600" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit User Dialog */}
      {selectedUser && (
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent data-testid="edit-user-dialog">
            <DialogHeader>
              <DialogTitle>Editar Usuario</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <Label htmlFor="edit_name">Nombre Completo</Label>
                <Input
                  id="edit_name"
                  value={selectedUser.full_name}
                  onChange={(e) => setSelectedUser({...selectedUser, full_name: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="edit_role">Rol</Label>
                <select
                  id="edit_role"
                  value={selectedUser.role_id}
                  onChange={(e) => setSelectedUser({...selectedUser, role_id: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {roles.map((role) => (
                    <option key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={selectedUser.active}
                  onChange={(e) => setSelectedUser({...selectedUser, active: e.target.checked})}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <label htmlFor="active" className="text-sm font-medium">
                  Usuario activo
                </label>
              </div>

              {!['master', 'superadmin', 'cliente'].includes(selectedUser.role_id) && (
                <div className="border border-[#E2E8F0] rounded-md p-3 bg-[#F8FAFC] space-y-2">
                  <Label className="text-xs flex items-center gap-1"><Building2 size={12} /> Empresa principal</Label>
                  <select
                    value={selectedUser.company_id || ''}
                    onChange={(e) => setSelectedUser({ ...selectedUser, company_id: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white"
                    data-testid="edit-user-company"
                  >
                    <option value="">— Sin empresa —</option>
                    {companies.map(c => <option key={c.company_id} value={c.company_id}>{c.name}</option>)}
                  </select>
                  <Label className="text-xs">Empresas adicionales autorizadas</Label>
                  <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                    {companies.filter(c => c.company_id !== selectedUser.company_id).map(c => {
                      const checked = (selectedUser.allowed_companies || []).includes(c.company_id);
                      return (
                        <button
                          type="button"
                          key={c.company_id}
                          onClick={() => toggleAllowedCompany(c.company_id)}
                          className={`px-2 py-1 rounded text-xs border ${checked ? 'bg-[#EEF2FF] border-[#4338CA] text-[#4338CA]' : 'bg-white border-[#E2E8F0] text-[#64748B]'}`}
                          data-testid={`edit-allowed-${c.company_id}`}
                        >
                          {checked ? '✓ ' : ''}{c.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!['master', 'superadmin', 'cliente'].includes(selectedUser.role_id) && (
                <div className="border border-[#E2E8F0] rounded-md p-3 bg-[#F8FAFC] space-y-3" data-testid="module-access-section">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs flex items-center gap-1"><Shield size={12} /> Acceso a módulos</Label>
                    {Array.isArray(selectedUser.enabled_modules) ? (
                      <button type="button" onClick={enableAllModules} className="text-[11px] text-primary font-medium hover:underline" data-testid="enable-all-modules">
                        Activar todos
                      </button>
                    ) : (
                      <span className="text-[11px] text-green-600 font-medium">Sin restricción (todos)</span>
                    )}
                  </div>
                  <p className="text-[11px] text-[#94A3B8]">Apaga los módulos que este usuario NO debe ver en su menú.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {APP_MODULES.map(m => {
                      const on = isModuleOn(m.key);
                      return (
                        <button
                          type="button"
                          key={m.key}
                          onClick={() => toggleModule(m.key)}
                          className={`flex items-center justify-between px-3 py-2 rounded-md border text-xs transition-colors ${on ? 'bg-white border-[#E2E8F0] text-[#022C22]' : 'bg-[#F1F5F9] border-[#E2E8F0] text-[#94A3B8]'}`}
                          data-testid={`module-toggle-${m.key}`}
                        >
                          <span>{m.label}</span>
                          <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? 'bg-primary' : 'bg-gray-300'}`}>
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full">
                Actualizar Usuario
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {users.length === 0 && (
        <div className="text-center py-12" data-testid="no-users-message">
          <p className="text-gray-600">No hay usuarios registrados</p>
        </div>
      )}

      {/* Ficha de empleado */}
      {profileUser && (
        <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="employee-profile-dialog">
            <DialogHeader>
              <DialogTitle>Ficha de empleado — {profileUser.full_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs font-semibold text-[#64748B] uppercase">Datos personales</p>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Cédula" value={profileForm.cedula} onChange={(e) => setProfileForm({ ...profileForm, cedula: e.target.value })} data-testid="pf-cedula" />
                <Input placeholder="Teléfono" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} data-testid="pf-phone" />
              </div>
              <Input placeholder="Dirección" value={profileForm.address} onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Fecha nacimiento</Label>
                  <Input type="date" value={profileForm.birthdate || ''} onChange={(e) => setProfileForm({ ...profileForm, birthdate: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Contacto emergencia</Label>
                  <Input value={profileForm.emergency_contact} onChange={(e) => setProfileForm({ ...profileForm, emergency_contact: e.target.value })} />
                </div>
              </div>
              <p className="text-xs font-semibold text-[#64748B] uppercase pt-1">Datos bancarios</p>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Banco" value={profileForm.bank_name} onChange={(e) => setProfileForm({ ...profileForm, bank_name: e.target.value })} data-testid="pf-bank" />
                <Input placeholder="Nº de cuenta" value={profileForm.bank_account} onChange={(e) => setProfileForm({ ...profileForm, bank_account: e.target.value })} />
              </div>
              <select value={profileForm.account_type} onChange={(e) => setProfileForm({ ...profileForm, account_type: e.target.value })} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white">
                <option value="ahorro">Ahorro</option>
                <option value="corriente">Corriente</option>
              </select>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Pago móvil (tel)" value={profileForm.mobile_payment_phone} onChange={(e) => setProfileForm({ ...profileForm, mobile_payment_phone: e.target.value })} data-testid="pf-mp-phone" />
                <Input placeholder="Pago móvil (cédula)" value={profileForm.mobile_payment_id} onChange={(e) => setProfileForm({ ...profileForm, mobile_payment_id: e.target.value })} />
              </div>
              <p className="text-xs font-semibold text-[#64748B] uppercase pt-1">Remuneración</p>
              <div className="grid grid-cols-2 gap-2">
                <Input type="number" step="0.01" placeholder="Salario ($)" value={profileForm.salary} onChange={(e) => setProfileForm({ ...profileForm, salary: e.target.value })} data-testid="pf-salary" />
                <Input type="number" step="0.01" placeholder="Comisión (%)" value={profileForm.commission_rate} onChange={(e) => setProfileForm({ ...profileForm, commission_rate: e.target.value })} />
              </div>
              <Input placeholder="Notas" value={profileForm.notes} onChange={(e) => setProfileForm({ ...profileForm, notes: e.target.value })} />
              <div className="flex gap-2 pt-1">
                <Button onClick={saveProfile} className="flex-1" data-testid="save-profile-button">Guardar ficha</Button>
                <Button variant="outline" onClick={() => { setProfileOpen(false); navigate('/payroll'); }} data-testid="pay-from-profile">
                  <Wallet size={16} className="mr-1.5" /> Pagar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
